import React, { useState } from 'react'

const CARDS = ['J', 'Q', 'K'] // index doubles as rank

// Exact Nash equilibrium for the second player in Kuhn poker. Unlike the first
// player's strategy (a one-parameter family), player two's is unique.
const aiFacingBet = (card) => {
  if (card === 2) return 'call' // K always calls
  if (card === 0) return 'fold' // J always folds
  return Math.random() < 1 / 3 ? 'call' : 'fold' // Q calls 1/3 of the time
}

const aiFacingCheck = (card) => {
  if (card === 2) return 'bet' // K always bets
  if (card === 0) return Math.random() < 1 / 3 ? 'bet' : 'check' // J bluffs 1/3
  return 'check' // Q never bets
}

const deal = () => {
  const deck = [0, 1, 2]
  const a = deck.splice(Math.floor(Math.random() * 3), 1)[0]
  const b = deck.splice(Math.floor(Math.random() * 2), 1)[0]
  return { you: a, ai: b }
}

// Player one's equilibrium value: -1/18 chips per hand. Perfect play still loses.
const GAME_VALUE = -1 / 18

const KuhnPoker = ({ onFinish }) => {
  const [hand, setHand] = useState(deal)
  const [phase, setPhase] = useState('act1') // act1 | facing-bet | over
  const [log, setLog] = useState([])
  const [chips, setChips] = useState(0)
  const [hands, setHands] = useState(0)
  const [reveal, setReveal] = useState(false)

  const settle = (delta, line) => {
    const nextChips = chips + delta
    const nextHands = hands + 1
    setChips(nextChips)
    setHands(nextHands)
    setLog((l) => [{ line, delta }, ...l].slice(0, 6))
    setPhase('over')
    setReveal(true)
    onFinish({
      game: 'Kuhn Poker',
      result: delta > 0 ? 'win' : delta < 0 ? 'loss' : 'draw',
      detail: `${delta > 0 ? '+' : ''}${delta} chips`,
    })
  }

  const showdown = (stake) => {
    const won = hand.you > hand.ai
    settle(
      won ? stake : -stake,
      `Showdown: your ${CARDS[hand.you]} vs ${CARDS[hand.ai]} — you ${won ? 'win' : 'lose'} ${stake}.`
    )
  }

  const onBet = () => {
    const move = aiFacingBet(hand.ai)
    if (move === 'fold') {
      setReveal(true)
      settle(1, `You bet. AI folds ${CARDS[hand.ai]} — you take the pot, +1.`)
    } else {
      showdown(2)
    }
  }

  const onCheck = () => {
    const move = aiFacingCheck(hand.ai)
    if (move === 'check') {
      showdown(1)
    } else {
      setPhase('facing-bet')
      setLog((l) => [{ line: 'You check. AI bets 1 — your call.', delta: null }, ...l].slice(0, 6))
    }
  }

  const onFold = () => {
    setReveal(true)
    settle(-1, `You fold. AI shows ${CARDS[hand.ai]}${hand.ai === 0 ? ' — bluffed off a J.' : '.'}`)
  }

  const nextHand = () => {
    setHand(deal())
    setPhase('act1')
    setReveal(false)
  }

  const benchmark = hands * GAME_VALUE

  return (
    <div className="game">
      <div className="game__board">
        <div className="felt">
          <div className="seat">
            <span className="seat__label">AI</span>
            <div className={`card ${reveal ? '' : 'card--back'}`}>{reveal ? CARDS[hand.ai] : '?'}</div>
          </div>
          <div className="pot">
            <span>pot</span>
            <strong>{phase === 'facing-bet' ? 3 : 2}</strong>
          </div>
          <div className="seat">
            <span className="seat__label">You</span>
            <div className="card">{CARDS[hand.you]}</div>
          </div>
        </div>

        <div className="game__actions">
          {phase === 'act1' && (
            <>
              <button className="btn btn-primary" onClick={onBet}>Bet 1</button>
              <button className="btn" onClick={onCheck}>Check</button>
            </>
          )}
          {phase === 'facing-bet' && (
            <>
              <button className="btn btn-primary" onClick={() => showdown(2)}>Call 1</button>
              <button className="btn" onClick={onFold}>Fold</button>
            </>
          )}
          {phase === 'over' && (
            <button className="btn btn-primary" onClick={nextHand}>Next hand</button>
          )}
        </div>

        <ul className="log">
          {log.map((entry, i) => (
            <li key={i} className={i === 0 ? 'log--fresh' : ''}>
              {entry.line}
            </li>
          ))}
        </ul>
      </div>

      <aside className="game__panel">
        <h4>Hands played: {hands}</h4>
        <div className="scoreline">
          <div>
            <span>Your chips</span>
            <strong className={chips >= 0 ? 'positive' : 'negative'}>
              {chips > 0 ? '+' : ''}{chips}
            </strong>
          </div>
          <div>
            <span>Perfect play</span>
            <strong className="muted">{benchmark.toFixed(2)}</strong>
          </div>
        </div>

        <h5>Why you can&rsquo;t win</h5>
        <p className="note">
          This AI plays Kuhn poker&rsquo;s <em>exact</em> Nash equilibrium: it folds J to a
          bet, always calls with K, calls Q one time in three, and bluffs a J into
          your check one time in three. The strategy is unexploitable, so no
          counter-strategy beats it.
        </p>
        <p className="note note--rule">
          Acting first is worth <strong>&minus;1/18 chips per hand</strong>. Flawless play
          still bleeds. The benchmark above is what a solver would have lost over
          your {hands} {hands === 1 ? 'hand' : 'hands'} &mdash; beating it is variance, not skill.
        </p>
      </aside>
    </div>
  )
}

export default KuhnPoker
